import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const Footer = () => {
  const { t, language } = useLanguage();

  const footerLinks = {
    services: {
      en: [
        { name: 'Consulting & Feasibility', href: '/services' },
        { name: '3D Design & Simulation', href: '/services' },
        { name: 'Show Visualization', href: '/services' },
        { name: 'Equipment Supply', href: '/services' },
        { name: 'Installation Assistance', href: '/services' },
        { name: 'Commissioning', href: '/services' },
      ],
      fr: [
        { name: 'Conseil & Faisabilité', href: '/services' },
        { name: 'Design 3D & Simulation', href: '/services' },
        { name: 'Visualisation de Spectacles', href: '/services' },
        { name: "Fourniture d'Équipements", href: '/services' },
        { name: "Assistance à l'Installation", href: '/services' },
        { name: 'Mise en Service', href: '/services' },
      ],
      es: [
        { name: 'Consultoría & Viabilidad', href: '/services' },
        { name: 'Diseño 3D & Simulación', href: '/services' },
        { name: 'Visualización de Shows', href: '/services' },
        { name: 'Suministro de Equipos', href: '/services' },
        { name: 'Asistencia en Instalación', href: '/services' },
        { name: 'Puesta en Marcha', href: '/services' },
      ],
    },
    solutions: {
      en: [
        { name: 'Show Fountains', href: '/solutions' },
        { name: 'Dry Deck Fountains', href: '/solutions' },
        { name: 'Urban Plaza Fountains', href: '/solutions' },
        { name: 'Hotel & Resort', href: '/solutions' },
        { name: 'Landmark Fountains', href: '/solutions' },
      ],
      fr: [
        { name: 'Fontaines Spectacle', href: '/solutions' },
        { name: 'Fontaines Sèches', href: '/solutions' },
        { name: 'Fontaines de Place Urbaine', href: '/solutions' },
        { name: 'Hôtels & Resorts', href: '/solutions' },
        { name: 'Fontaines Monument', href: '/solutions' },
      ],
      es: [
        { name: 'Fuentes Espectáculo', href: '/solutions' },
        { name: 'Fuentes Secas', href: '/solutions' },
        { name: 'Fuentes de Plaza Urbana', href: '/solutions' },
        { name: 'Hoteles & Resorts', href: '/solutions' },
        { name: 'Fuentes Monumento', href: '/solutions' },
      ],
    },
  };

  const regions = {
    en: ['Europe', 'North Africa', 'Africa', 'Mediterranean', 'Middle East', 'Gulf Region', 'International'],
    fr: ['Europe', 'Afrique du Nord', 'Afrique', 'Méditerranée', 'Moyen-Orient', 'Région du Golfe', 'International'],
    es: ['Europa', 'Norte de África', 'África', 'Mediterráneo', 'Oriente Medio', 'Región del Golfo', 'Internacional'],
  };

  const companyLinks = [
    { name: t('nav.about'), href: '/about' },
    { name: t('nav.process'), href: '/process' },
    { name: t('nav.consultants'), href: '/consultants' },
    { name: t('nav.contact'), href: '/contact' },
  ];

  const currentServices = footerLinks.services[language] || footerLinks.services.en;
  const currentSolutions = footerLinks.solutions[language] || footerLinks.solutions.en;
  const currentRegions = regions[language] || regions.en;

  return (
    <footer data-testid="footer" className="bg-[#1A1A1A] text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="bg-white p-3 rounded-sm inline-block mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_engineering-water/artifacts/z1g41hzx_logo%20ecrit%20magnum.jpg"
                alt="Magnum Fountain SL"
                className="h-12 w-auto"
              />
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-sm">
              {t('footer.description')}
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3 text-sm text-gray-400">
                <MapPin className="w-4 h-4 mt-0.5 text-[#29ABE2] flex-shrink-0" />
                <span>Calle la Fundicion 4 Bis, Nave 64<br />28529 Rivas Vaciamadrid<br />Madrid, Spain</span>
              </div>
              <a
                href="tel:+34919933440"
                className="flex items-center gap-3 text-sm text-gray-400 hover:text-[#29ABE2] transition-colors"
              >
                <Phone className="w-4 h-4 text-[#29ABE2]" />
                <span>+34 919 933 440</span>
              </a>
              <a
                href="mailto:info@magnumfountain.com"
                className="flex items-center gap-3 text-sm text-gray-400 hover:text-[#29ABE2] transition-colors"
              >
                <Mail className="w-4 h-4 text-[#29ABE2]" />
                <span>info@magnumfountain.com</span>
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider mb-6 text-white">
              {t('footer.services')}
            </h4>
            <ul className="space-y-3">
              {currentServices.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-gray-400 hover:text-[#29ABE2] transition-colors footer-link"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Solutions */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider mb-6 text-white">
              {t('footer.solutions')}
            </h4>
            <ul className="space-y-3">
              {currentSolutions.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-gray-400 hover:text-[#29ABE2] transition-colors footer-link"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider mb-6 text-white">
              {t('footer.company')}
            </h4>
            <ul className="space-y-3">
              {companyLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-gray-400 hover:text-[#29ABE2] transition-colors footer-link"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Regions Served */}
        <div className="mt-16 pt-8 border-t border-gray-800">
          <h4 className="font-semibold text-sm uppercase tracking-wider mb-4 text-white">
            {t('footer.regionsServed')}
          </h4>
          <div className="flex flex-wrap gap-2">
            {currentRegions.map((region) => (
              <span
                key={region}
                className="px-3 py-1 text-xs text-gray-400 border border-gray-700 rounded-full"
              >
                {region}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-gray-500">
              {t('footer.copyright')}
            </p>
            <div className="flex items-center gap-6">
              <span className="text-xs text-gray-500">
                {t('footer.tagline')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/34607798985"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-float-btn"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg whatsapp-pulse hover:scale-110 transition-transform"
      >
        <svg viewBox="0 0 24 24" className="w-7 h-7 text-white fill-current">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </footer>
  );
};
